import { z } from 'zod';
import { tagInfo } from './registry.js';
/**
 * Per-tag numbers precomputed after each load (scripts/gen-tag-stats.ts) so
 * the dictionary and Tags pages never query 150M permits on a click (#171,
 * #172): how often each tag fires over the last 10 years, plus example permits.
 */
export const TagExample = z.object({
    permit_id: z.string(),
    source_permit_number: z.string().nullable(),
    description: z.string(),
    type: z.string().nullable(),
    city: z.string().nullable(),
    state: z.string().nullable(),
});
const Counts = z.record(z.string(), z.number().int().nonnegative());
export const TagStat = z.object({
    count_10y: z.number().int().nonnegative(),
    /** count_10y ÷ all permits dated in the last 10 years. */
    share_10y: z.number().min(0).max(1),
    examples: z.array(TagExample),
    // Tags page (#172). Defaults keep an older snapshot parsing.
    /** Permits carrying the tag, all years. */
    count_all: z.number().int().nonnegative().default(0),
    /** …dated in the last 12 months. */
    count_12mo: z.number().int().nonnegative().default(0),
    /** Last 5 calendar years: [year, permits]. */
    years: z.array(z.tuple([z.number().int(), z.number().int().nonnegative()])).default([]),
    /** Last-12-month permits with the tag, by state (USPS) and by county FIPS. */
    by_state: Counts.default({}),
    by_county: Counts.default({}),
});
export const TagStats = z.object({
    generated_at: z.string(),
    /** The served permits table the numbers came from. */
    table: z.string(),
    permits_10y: z.number().int().nonnegative(),
    /** All permits, any date (the "share of all permits" denominator). */
    permits_all: z.number().int().nonnegative().default(0),
    /** Rate denominators: people in jurisdictions we cover. */
    state_coverage: z
        .record(z.string(), z.object({ covered_population: z.number(), total_population: z.number() }))
        .default({}),
    county_coverage: z
        .record(z.string(), z.object({ name: z.string(), state: z.string(), covered_population: z.number() }))
        .default({}),
    tags: z.record(z.string(), TagStat),
});
/** A permit's tags minus anything not in the molluskio vocabulary (record_exclude:*). */
export function customerTags(tags) {
    return (tags ?? []).filter((t) => tagInfo(t) !== undefined);
}
const num = (v) => Number(v ?? 0) || 0;
export function buildTagStats(rows, meta) {
    const total = num(rows[0]?.permits_10y);
    const tags = {};
    for (const r of rows) {
        if (!tagInfo(r.tag))
            continue;
        const count = num(r.count_10y);
        tags[r.tag] = {
            count_10y: count,
            share_10y: total > 0 ? count / total : 0,
            examples: z
                .array(TagExample)
                .parse(r.examples ?? [])
                .slice(0, 3),
            count_all: num(r.count_all),
            count_12mo: num(r.count_12mo),
            years: (r.years ?? [])
                .map((y) => [num(y.y), num(y.n)])
                .sort((a, b) => a[0] - b[0]),
            by_state: Object.fromEntries((r.states ?? []).map((x) => [x.st, num(x.n)])),
            by_county: Object.fromEntries((r.counties ?? []).map((x) => [x.fips, num(x.n)])),
        };
    }
    return TagStats.parse({
        generated_at: meta.generatedAt,
        table: meta.table,
        permits_10y: total,
        permits_all: num(rows[0]?.permits_all),
        state_coverage: meta.stateCoverage ?? {},
        county_coverage: meta.countyCoverage ?? {},
        tags,
    });
}
//# sourceMappingURL=stats.js.map